import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { uploadAPI } from "@/lib/api";
import { Upload, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function PdfUploader({ onTextExtracted, label = "Upload Document (PDF or DOCX)" }) {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState(null);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    const isPdf = name.endsWith(".pdf");
    const isDocx = name.endsWith(".docx");
    if (!isPdf && !isDocx) {
      toast.error("Unable to read document. Please upload a valid PDF or DOCX file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setUploading(true);
    setFileName(file.name);
    try {
      const res = await uploadAPI.pdf(file);
      // Pass both text and filename to callback
      onTextExtracted(res.data.text, file.name);
      toast.success(`Extracted text from ${res.data.pages} page${res.data.pages > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to read document. Please upload a valid PDF or DOCX file.");
      setFileName(null);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleClear = () => {
    setFileName(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="flex items-center gap-2">
      <input ref={inputRef} type="file" accept=".pdf,.docx" onChange={handleFile} className="hidden" data-testid="pdf-upload-input" />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-[#003087] text-[#003087] hover:bg-[#F0F4F8]"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid="pdf-upload-btn"
      >
        {uploading ? (
          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Extracting...</>
        ) : (
          <><Upload className="w-3.5 h-3.5 mr-1.5" /> {label}</>
        )}
      </Button>
      {fileName && (
        <span className="text-xs text-slate-500 flex items-center gap-1">
          <FileText className="w-3 h-3" /> {fileName}
          <button onClick={handleClear} className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>
        </span>
      )}
    </div>
  );
}
